import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { Button } from './Button';

/*
  The one surface in the product that casts a shadow, because it is the one
  surface that genuinely floats above the page.

  Also the one that traps focus: an open dialog whose Tab order walks out into
  the page behind it is unusable with a keyboard, and this dashboard's dialogs
  are where students get added and codes get revoked.
*/

const sizes = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
} as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  size?: keyof typeof sizes;
  /** A right-aligned action row under a divider. */
  footer?: ReactNode;
  children: ReactNode;
}

export function Modal({ open, onClose, title, description, size = 'md', footer, children }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  /*
    ── Why onClose lives in a ref ──────────────────────────────────────────────

    It is almost always passed as an inline arrow — `onClose={() => setOpen(false)}`
    — so it has a new identity on every render of the page behind this dialog.
    With it in a dependency array, ANY re-render tore the effect down and built
    it again. In a dialog with a controlled input that is every keystroke, and
    each rebuild re-ran the autofocus below.

    The symptom was precise and baffling: type one letter, lose the caret, and
    find the close button focused. `querySelector` returns the first match in
    DOCUMENT order regardless of how the selector is written, and the close
    button is in the header, above the form. So every letter typed sent the
    caret to X.

    Read through a ref, the handler always sees the current onClose and the
    effects depend only on `open` — which changes twice in a dialog's life.
  */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /* Set briefly when somebody clicks the backdrop — see the note there. */
  const [nudge, setNudge] = useState(false);

  /* ── Scroll lock, and giving focus back when it closes ──────────────────── */
  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
      /* Runs once, on close — not on every keystroke, which is what used to
         yank the caret back to the button that opened the dialog. */
      previouslyFocused?.focus?.();
    };
  }, [open]);

  /* ── Escape, and keeping Tab inside the dialog ──────────────────────────── */
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  /*
    ── Where the caret goes when the dialog opens ──────────────────────────────

    A field if there is one, and only then a button. A dialog that exists to be
    typed into should not open with the caret on "close" — which is what the
    old selector did, because it asked for the first of input/select/textarea/
    button and got whichever came first in the markup.
  */
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const field = panel.querySelector<HTMLElement>(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])',
    );
    (field ?? panel.querySelector<HTMLElement>('button:not([disabled])'))?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 sm:items-center">
      {/*
        ── Clicking outside does NOT close this ────────────────────────────

        Every dialog in this product is either a form somebody is part-way
        through or a confirmation of something irreversible. A stray click
        beside one used to throw the typed work away silently — a subject
        half-named, a staff record half-corrected, forty marks of context
        gone — with no undo and no warning.

        So the backdrop acknowledges the click instead of acting on it. The
        pulse matters: a backdrop that simply does nothing reads as a frozen
        page, and the next thing somebody does is click it harder. Escape and
        the close button still work, because those are deliberate.
      */}
      <div
        className="fixed inset-0 bg-[hsl(218_52%_8%_/_0.55)] animate-fade-in"
        onClick={() => {
          setNudge(true);
          window.setTimeout(() => setNudge(false), 240);
        }}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby={description ? 'modal-description' : undefined}
        className={cn(
          'relative z-10 my-auto w-full bg-card rounded-lg border border-border shadow-overlay',
          nudge ? 'animate-attention' : 'animate-slide-up',
          sizes[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h2 id="modal-title" className={styles.sectionTitle}>
              {title}
            </h2>
            {description && (
              <p id="modal-description" className={cn(styles.meta, 'mt-0.5')}>
                {description}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" iconOnly aria-label="Close" onClick={onClose} icon={<X size={14} />} />
        </div>

        <div className="px-4 py-4">{children}</div>

        {footer && (
          <div className={cn(styles.panelRow, 'flex items-center justify-end gap-2')}>{footer}</div>
        )}
      </div>
    </div>
  );
}
